import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  serverTimestamp,
  orderBy,
  limit as firestoreLimit,
  GeoPoint,
  DocumentData,
  QueryConstraint,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, db } from "@/config/firebase";
import {
  BarSuggestion,
  BarSuggestionStatus,
  BarStatus,
  BarVisibility,
} from "@/types";
import ngeohash from "ngeohash";

const COLLECTION = "barSuggestions";
const BARS_COLLECTION = "bars";
const NOMINATIM_SEARCH_URL = "/api/nominatim/search";
const GEOHASH_PRECISION = 7;

export interface GeocodeCandidate {
  latitude: number;
  longitude: number;
  address?: string;
  city?: string;
  postalCode?: string;
  displayName: string;
}

type NominatimAddress = {
  road?: string;
  pedestrian?: string;
  footway?: string;
  path?: string;
  square?: string;
  house_number?: string;
  city?: string;
  town?: string;
  village?: string;
  municipality?: string;
  postcode?: string;
};

type NominatimSearchResult = {
  lat: string;
  lon: string;
  display_name?: string;
  address?: NominatimAddress;
};

const isLocalDev =
  typeof window !== "undefined" &&
  /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);

const buildSearchGeocodeUrl = (params: URLSearchParams) =>
  isLocalDev
    ? `${NOMINATIM_SEARCH_URL}?${params}`
    : `https://nominatim.openstreetmap.org/search?${params}`;

const buildStreetAddress = (address?: NominatimAddress): string | undefined => {
  if (!address) return undefined;
  const street =
    address.road ||
    address.pedestrian ||
    address.footway ||
    address.path ||
    address.square;
  if (!street) return undefined;
  return [street, address.house_number].filter(Boolean).join(" ").trim() || undefined;
};

const buildCity = (address?: NominatimAddress) =>
  address?.city || address?.town || address?.village || address?.municipality;

const getAddressDetails = (suggestion: BarSuggestion) => {
  const address = suggestion.address || "";
  const postalCode = suggestion.postalCode || "";
  const city = suggestion.city || "";

  const houseNumberMatch = address.match(/\b\d+[a-zA-Z]?\b(?:\s*[/-]\s*\d+[a-zA-Z]?)?$/);
  const housenumber = houseNumberMatch?.[0]?.trim() || "";
  const street = housenumber
    ? address.slice(0, Math.max(0, address.length - housenumber.length)).trim().replace(/[\s,]+$/, "")
    : address.trim();

  return { street, housenumber, city, postcode: postalCode, country: "DE" };
};

const notifySuggestionUser = async (params: {
  userId: string;
  type: "contribution_approved" | "contribution_rejected";
  title: string;
  body: string;
  data?: Record<string, string>;
}): Promise<void> => {
  const { userId, type, title, body, data } = params;
  if (!userId) return;

  try {
    await addDoc(collection(db, "users", userId, "notifications"), {
      type,
      title,
      message: body,
      isRead: false,
      target: "user",
      createdAt: serverTimestamp(),
      ...(data ?? {}),
    });
  } catch (error) {
    console.warn("Suggestion notification inbox write failed:", error);
  }

  try {
    const fns = getFunctions(app, "us-central1");
    const callable = httpsCallable<
      { userId: string; title: string; body: string; type: string; data?: Record<string, string> },
      { successCount: number; failureCount: number; totalTokens: number }
    >(fns, "sendTestNotification");
    await callable({ userId, title, body, type, data });
  } catch (error) {
    console.warn("Suggestion push notification failed:", error);
  }
};

export const barSuggestionsService = {
  getSuggestions: async (
    status?: BarSuggestionStatus,
    limitCount?: number,
  ): Promise<BarSuggestion[]> => {
    try {
      const ref = collection(db, COLLECTION);
      const constraints: QueryConstraint[] = [orderBy("createdAt", "desc")];
      if (status) constraints.unshift(where("status", "==", status));
      if (limitCount) constraints.push(firestoreLimit(limitCount));

      const snapshot = await getDocs(query(ref, ...constraints));
      return snapshot.docs.map((doc: DocumentData) => ({
        id: doc.id,
        ...doc.data(),
      })) as BarSuggestion[];
    } catch (error) {
      console.error("Error fetching bar suggestions:", error);
      throw new Error("Fehler beim Laden der Bar-Vorschläge");
    }
  },

  getSuggestion: async (id: string): Promise<BarSuggestion | null> => {
    try {
      const ref = doc(db, COLLECTION, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() } as BarSuggestion;
    } catch (error) {
      console.error("Error fetching bar suggestion:", error);
      throw new Error("Fehler beim Laden des Vorschlags");
    }
  },

  geocodeAddress: async (queryString: string): Promise<GeocodeCandidate[]> => {
    const trimmed = queryString.trim();
    if (!trimmed) return [];

    try {
      const params = new URLSearchParams({
        q: trimmed,
        format: "jsonv2",
        addressdetails: "1",
        limit: "6",
        "accept-language": "de",
      });

      const response = await fetch(buildSearchGeocodeUrl(params), {
        headers: isLocalDev ? undefined : { "Accept-Language": "de" },
      });

      if (!response.ok) throw new Error(`Geocoding failed with ${response.status}`);

      const data = (await response.json()) as NominatimSearchResult[];

      return data
        .map((entry): GeocodeCandidate | null => {
          const latitude = Number(entry.lat);
          const longitude = Number(entry.lon);
          if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
          return {
            latitude,
            longitude,
            address: buildStreetAddress(entry.address),
            city: buildCity(entry.address),
            postalCode: entry.address?.postcode,
            displayName: entry.display_name || trimmed,
          };
        })
        .filter((c): c is GeocodeCandidate => c !== null);
    } catch (error) {
      console.error("Error geocoding address:", error);
      throw new Error("Fehler beim Geokodieren der Adresse");
    }
  },

  applyGeocodedLocation: async (
    suggestionId: string,
    adminUid: string,
    candidate: GeocodeCandidate,
    options?: { keepOsmId?: boolean },
  ): Promise<void> => {
    try {
      const ref = doc(db, COLLECTION, suggestionId);
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error("Vorschlag nicht gefunden");

      await updateDoc(ref, {
        latitude: candidate.latitude,
        longitude: candidate.longitude,
        location: new GeoPoint(candidate.latitude, candidate.longitude),
        address: candidate.address ?? null,
        city: candidate.city ?? null,
        postalCode: candidate.postalCode ?? null,
        // Beim Übernehmen eines geokodierten Treffers wird die Position vom
        // OSM-Punkt weg verschoben → osmId verfällt. Beim Beibehalten der
        // eingereichten Koordinaten bleibt die OSM-Verknüpfung erhalten.
        ...(options?.keepOsmId ? {} : { osmId: null }),
        manualLocationOverride: true,
        locationUpdatedAt: serverTimestamp(),
        locationUpdatedBy: adminUid,
      });
    } catch (error) {
      console.error("Error applying geocoded location:", error);
      throw new Error(
        `Fehler beim Übernehmen des Standorts: ${error instanceof Error ? error.message : "Unbekannter Fehler"}`,
      );
    }
  },

  getSuggestionsInBounds: async (
    bounds: { south: number; west: number; north: number; east: number },
    statuses?: BarSuggestionStatus[],
  ): Promise<BarSuggestion[]> => {
    const statusList = statuses && statuses.length > 0 ? statuses : [undefined];
    const results = await Promise.all(
      statusList.map((status) => barSuggestionsService.getSuggestions(status, 100)),
    );
    return results.flat().filter(
      (s) =>
        s.latitude >= bounds.south &&
        s.latitude <= bounds.north &&
        s.longitude >= bounds.west &&
        s.longitude <= bounds.east,
    );
  },

  getStats: async (): Promise<{ total: number; pending: number; approved: number; rejected: number }> => {
    try {
      const ref = collection(db, COLLECTION);
      const [allSnap, pendingSnap, approvedSnap, rejectedSnap] = await Promise.all([
        getDocs(query(ref)),
        getDocs(query(ref, where("status", "==", BarSuggestionStatus.PENDING))),
        getDocs(query(ref, where("status", "==", BarSuggestionStatus.APPROVED))),
        getDocs(query(ref, where("status", "==", BarSuggestionStatus.REJECTED))),
      ]);
      return {
        total: allSnap.size,
        pending: pendingSnap.size,
        approved: approvedSnap.size,
        rejected: rejectedSnap.size,
      };
    } catch (error) {
      console.error("Error fetching suggestion stats:", error);
      throw new Error("Fehler beim Laden der Statistiken");
    }
  },

  approveSuggestion: async (
    suggestionId: string,
    adminUid: string,
  ): Promise<string> => {
    try {
      const suggRef = doc(db, COLLECTION, suggestionId);
      const suggSnap = await getDoc(suggRef);
      if (!suggSnap.exists()) throw new Error("Vorschlag nicht gefunden");

      const suggestion = { id: suggSnap.id, ...suggSnap.data() } as BarSuggestion;

      if (suggestion.status !== BarSuggestionStatus.PENDING) {
        throw new Error("Vorschlag wurde bereits bearbeitet");
      }

      const addressDetails = getAddressDetails(suggestion);
      const geohash = ngeohash.encode(suggestion.latitude, suggestion.longitude, GEOHASH_PRECISION);

      const barData = {
        name: suggestion.barName,
        address: suggestion.address || null,
        city: suggestion.city || null,
        postalCode: suggestion.postalCode || null,
        country: "DE",
        latitude: suggestion.latitude,
        longitude: suggestion.longitude,
        location: new GeoPoint(suggestion.latitude, suggestion.longitude),
        geohash,
        addressDetails,
        osmId: suggestion.osmId || null,
        source: "user_suggestion",
        sourceSuggestionId: suggestion.id,
        sourceSuggestionStatus: suggestion.status,
        importedFromSuggestion: true,
        status: BarStatus.ACTIVE,
        visibility: BarVisibility.PUBLIC,
        isVerified: false,
        isClaimed: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        tags: [],
        categories: [],
        viewCount: 0,
        favoriteCount: 0,
        drinkCount: 0,
        eventCount: 0,
        offerCount: 0,
      };

      const barRef = await addDoc(collection(db, BARS_COLLECTION), barData);

      await updateDoc(suggRef, {
        status: BarSuggestionStatus.APPROVED,
        reviewedBy: adminUid,
        reviewedAt: serverTimestamp(),
        createdBarId: barRef.id,
      });

      await notifySuggestionUser({
        userId: suggestion.userId,
        type: "contribution_approved",
        title: "Bar-Vorschlag genehmigt",
        body: `„${suggestion.barName}" wurde freigegeben und ist jetzt in Kneipster verfügbar. Danke für deinen Beitrag!`,
        data: { barId: barRef.id, suggestionId: suggestion.id },
      });

      return barRef.id;
    } catch (error) {
      console.error("Error approving suggestion:", error);
      throw new Error(
        `Fehler beim Genehmigen: ${error instanceof Error ? error.message : "Unbekannter Fehler"}`,
      );
    }
  },

  rejectSuggestion: async (
    suggestionId: string,
    adminUid: string,
    reason: string,
  ): Promise<void> => {
    try {
      const ref = doc(db, COLLECTION, suggestionId);
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error("Vorschlag nicht gefunden");

      const data = snap.data();
      if (data?.status !== BarSuggestionStatus.PENDING) {
        throw new Error("Vorschlag wurde bereits bearbeitet");
      }

      await updateDoc(ref, {
        status: BarSuggestionStatus.REJECTED,
        reviewedBy: adminUid,
        reviewedAt: serverTimestamp(),
        rejectionReason: reason,
      });

      const barName = (data?.barName as string | undefined) || "Dein Bar-Vorschlag";
      await notifySuggestionUser({
        userId: data?.userId as string,
        type: "contribution_rejected",
        title: "Bar-Vorschlag abgelehnt",
        body: `„${barName}" wurde leider nicht freigegeben. Grund: ${reason}`,
        data: { suggestionId, rejectionReason: reason },
      });
    } catch (error) {
      console.error("Error rejecting suggestion:", error);
      throw new Error("Fehler beim Ablehnen des Vorschlags");
    }
  },

  markAsDuplicate: async (
    suggestionId: string,
    adminUid: string,
    existingBarId: string,
  ): Promise<void> => {
    try {
      const ref = doc(db, COLLECTION, suggestionId);
      await updateDoc(ref, {
        status: BarSuggestionStatus.DUPLICATE,
        reviewedBy: adminUid,
        reviewedAt: serverTimestamp(),
        rejectionReason: `Duplikat von Bar: ${existingBarId}`,
        createdBarId: existingBarId,
      });
    } catch (error) {
      console.error("Error marking as duplicate:", error);
      throw new Error("Fehler beim Markieren als Duplikat");
    }
  },

  /**
   * Führt einen Vorschlag mit einer bereits bestehenden Bar zusammen, statt
   * eine neue anzulegen (z. B. wenn die Bar nur unbenannt importiert wurde).
   * Überträgt die per `options` ausgewählten Felder auf die bestehende Bar,
   * markiert den Vorschlag als erledigt und benachrichtigt den Nutzer positiv.
   */
  mergeIntoExistingBar: async (
    suggestionId: string,
    adminUid: string,
    targetBarId: string,
    options: {
      applyName: boolean;
      applyLocation: boolean;
      applyAddress: boolean;
      verify: boolean;
    },
  ): Promise<void> => {
    try {
      const suggRef = doc(db, COLLECTION, suggestionId);
      const suggSnap = await getDoc(suggRef);
      if (!suggSnap.exists()) throw new Error("Vorschlag nicht gefunden");
      const suggestion = { id: suggSnap.id, ...suggSnap.data() } as BarSuggestion;

      const barRef = doc(db, BARS_COLLECTION, targetBarId);
      const barSnap = await getDoc(barRef);
      if (!barSnap.exists()) throw new Error("Ziel-Bar nicht gefunden");

      const barUpdates: Record<string, unknown> = { updatedAt: serverTimestamp() };

      if (options.applyName && suggestion.barName) {
        barUpdates.name = suggestion.barName;
      }
      if (options.applyLocation) {
        barUpdates.latitude = suggestion.latitude;
        barUpdates.longitude = suggestion.longitude;
        barUpdates.location = new GeoPoint(suggestion.latitude, suggestion.longitude);
        barUpdates.geohash = ngeohash.encode(
          suggestion.latitude,
          suggestion.longitude,
          GEOHASH_PRECISION,
        );
      }
      if (options.applyAddress) {
        barUpdates.address = suggestion.address || null;
        barUpdates.city = suggestion.city || null;
        barUpdates.postalCode = suggestion.postalCode || null;
        barUpdates.addressDetails = getAddressDetails(suggestion);
      }
      if (options.verify) {
        barUpdates.isVerified = true;
        barUpdates.verifiedAt = serverTimestamp();
        barUpdates.verifiedBy = adminUid;
      }

      await updateDoc(barRef, barUpdates);

      await updateDoc(suggRef, {
        status: BarSuggestionStatus.DUPLICATE,
        reviewedBy: adminUid,
        reviewedAt: serverTimestamp(),
        createdBarId: targetBarId,
        mergedIntoBarId: targetBarId,
        rejectionReason: `Mit bestehender Bar zusammengeführt: ${targetBarId}`,
      });

      await notifySuggestionUser({
        userId: suggestion.userId,
        type: "contribution_approved",
        title: "Bar-Vorschlag übernommen",
        body: `„${suggestion.barName}" wurde mit einem bestehenden Eintrag in Kneipster zusammengeführt. Danke für deinen Beitrag!`,
        data: { barId: targetBarId, suggestionId: suggestion.id },
      });
    } catch (error) {
      console.error("Error merging suggestion into bar:", error);
      throw new Error(
        `Fehler beim Zusammenführen: ${error instanceof Error ? error.message : "Unbekannter Fehler"}`,
      );
    }
  },

  deleteSuggestion: async (suggestionId: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, COLLECTION, suggestionId));
    } catch (error) {
      console.error("Error deleting suggestion:", error);
      throw new Error("Fehler beim Löschen des Vorschlags");
    }
  },
};
